import { Box, IconButton, Separator, Stack, Text } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import React from "react"
import ShareCard from "../ShareCard"
import InactiveSchoolGate from "../gate/InactiveSchoolGate"
import useGatedShareAccessKey from "../gate/useGatedShareAccessKey"
import { useCopyLink } from "../link/CopyLink"
import RadioPills from "../link/RadioPills"
import ShareLink from "../link/ShareLink"
import AtmosphericImage from "../shared/AtmosphericImage"
import RoleDescriptorsDrawer from "../shared/RoleDescriptorsDrawer"
import { useShareDataContext } from "../shared/ShareDataContextProvider"
import {
  getInviteRoleInt,
  getInviteRoleUrl,
  useRolesConfigWithLabels,
  getGateReasonTooltip
} from "../shared/rolesConfig"

const tPrefix = "share_card.link_tab"

// Determine if user can invite to any pill-worthy role - that is,
// should we show the pills for the roles which the user can invite to or not.
// This may need some work.
const hasPillworthyRoles = (roles, context) => {
  if (roles) {
    switch (context.type) {
      case "cohort":
        return roles.some(
          (role) =>
            !["cohort_viewer", "cohort_observer", "cohort_learner"].includes(
              role
            )
        )
      case "group":
        return true
      case "school":
        console.warn(`Share context for "${context.type}" not implemented yet`)
        return false
    }
  }

  return false
}

const LinkTab = () => {
  const { context, permissions } = useShareDataContext()
  const gatedReason = permissions[useGatedShareAccessKey()]?.gated_reason
  const { t, tc } = useTranslate()
  const rolesConfig = useRolesConfigWithLabels()
  const inviteUrls = permissions.invite_roles
  const handleCopy = useCopyLink()
  const handleCopyInviteUrl = (inviteRole) => {
    // We don't want to allow copying the links when the school is inactive
    // since they can't add people to cohorts.
    if (gatedReason !== "SCHOOL_INACTIVE") {
      handleCopy(getInviteRoleUrl(inviteUrls[inviteRole]))
    }
  }

  const filteredInviteRoleOptions = React.useMemo(() => {
    function getRoleVal(key) {
      const role = getInviteRoleInt(inviteUrls[key])
      return role === null || role === undefined ? -Infinity : role
    }

    // Get the sorted list of roles that user can invite to.
    let roles = Object.keys(rolesConfig).sort((keyA, keyB) => {
      return getRoleVal(keyA) - getRoleVal(keyB)
    })

    // If no pill-worthy roles, limit to last role (which has greatest role).
    // NOTE: limiting to only 1 role ensures role pills are not displayed.
    if (hasPillworthyRoles(roles, context)) {
      // Filter out roles with mocked roles so we don't list them in the pill buttons.
      roles = roles.filter((key) => getRoleVal(key) !== -Infinity)
    } else {
      roles = roles.slice(-1)
    }

    // Build config back up.
    return roles.reduce((acc, key) => {
      const config = rolesConfig[key]
      const tooltip = getGateReasonTooltip(config, t)

      acc.push({
        key,
        tooltip,
        ...config
      })
      return acc
    }, [])
  }, [rolesConfig, context, inviteUrls, t])

  // Defaulting to the first role option.
  const getDefaultInviteRole = () => {
    const studentRoleOption = filteredInviteRoleOptions.find(
      (config) => config.isDefault
    )
    const roleOption = studentRoleOption || filteredInviteRoleOptions[0]
    return roleOption?.key
  }

  const [inviteRole, setInviteRole] = React.useState(getDefaultInviteRole)

  React.useEffect(() => {
    if (!inviteRole) setInviteRole(getDefaultInviteRole)
  }, [filteredInviteRoleOptions])

  const inviteRoleOption = React.useMemo(
    () => filteredInviteRoleOptions.find((config) => config.key === inviteRole),
    [inviteRole]
  )
  const inviteUrl = inviteUrls && getInviteRoleUrl(inviteUrls[inviteRole])

  if (!inviteRole) return null

  return (
    <Box mx={3} mb={3}>
      <AtmosphericImage>
        <Box px={"var(--card-padding)"} pt={"var(--card-padding)"}>
          <ShareCard />

          {filteredInviteRoleOptions.length > 1 ? (
            <Stack gap={1}>
              <Text
                size="xs"
                color="gray.500"
                textAlign="center"
                fontWeight={500}
                mt={4}
                mb={0}
              >
                {t(`${tPrefix}.Invite to`)}...
              </Text>
              <RadioPills
                options={filteredInviteRoleOptions}
                value={inviteRole}
                onChange={(role) => {
                  setInviteRole(role)
                  handleCopyInviteUrl(role)
                }}
                name="invite-permissions"
              />
              {!!inviteRoleOption && (
                <Text
                  size="xs"
                  color="gray.500"
                  textAlign="center"
                  m={0}
                  px={8}
                >
                  {tc(`${tPrefix}.roles.${inviteRoleOption.key}.subtitle`)}{" "}
                  <RoleDescriptorsDrawer>
                    {({ ref, onOpen }) => (
                      <IconButton
                        ref={ref}
                        children={<Pathicon icon="info-circle" size=".8em" />}
                        onClick={onOpen}
                        variant="plain"
                        color="gray.500"
                        p={0}
                        minW={0}
                      />
                    )}
                  </RoleDescriptorsDrawer>
                </Text>
              )}
            </Stack>
          ) : null}
        </Box>

        <Separator mt={4} borderBottomWidth={0} borderTopWidth="1px" />

        <InactiveSchoolGate>
          <Box px={"var(--card-padding)"} pb={"var(--card-padding)"} pt={4}>
            <ShareLink url={inviteUrl} />
          </Box>
        </InactiveSchoolGate>
      </AtmosphericImage>
    </Box>
  )
}

export default LinkTab
